/**
 * AdminLayout.js - 后台管理布局组件
 * 提供带侧边栏导航的整体框架，包含 Header、Sider 和 Content 区域
 */
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, Menu, Button, Typography, Space, theme, Avatar } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  OrderedListOutlined,
  UserOutlined,
  TagOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  MailOutlined,
  DatabaseOutlined,
  CloudUploadOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// 侧边栏导航菜单配置
const menuItems = [
  {
    key: '/admin',
    icon: <DashboardOutlined />,
    label: <Link to="/admin">仪表盘</Link>,
  },
  {
    key: '/admin/products',
    icon: <ShoppingOutlined />,
    label: <Link to="/admin/products">商品管理</Link>,
  },
  {
    key: '/admin/categories',
    icon: <AppstoreOutlined />,
    label: <Link to="/admin/categories">分类管理</Link>,
  },
  {
    key: '/admin/orders',
    icon: <OrderedListOutlined />,
    label: <Link to="/admin/orders">订单管理</Link>,
  },
  {
    key: '/admin/abandoned-orders',
    icon: <BookOutlined />,
    label: <Link to="/admin/abandoned-orders">弃单管理</Link>,
  },
  {
    key: '/admin/recent-carts',
    icon: <ShoppingCartOutlined />,
    label: <Link to="/admin/recent-carts">最近购物车</Link>,
  },
  {
    key: '/admin/customers',
    icon: <UserOutlined />,
    label: <Link to="/admin/customers">客户管理</Link>,
  },
  {
    key: '/admin/customer-tags',
    icon: <TagOutlined />,
    label: <Link to="/admin/customer-tags">客户标签</Link>,
  },
  {
    key: '/admin/customer-segments',
    icon: <UserOutlined />,
    label: <Link to="/admin/customer-segments">客户细分</Link>,
  },
  {
    key: '/admin/email-campaigns',
    icon: <MailOutlined />,
    label: <Link to="/admin/email-campaigns">邮件营销</Link>,
  },
  {
    key: '/admin/inventory',
    icon: <DatabaseOutlined />,
    label: <Link to="/admin/inventory">库存管理</Link>,
  },
  {
    key: '/admin/discounts',
    icon: <TagOutlined />,
    label: <Link to="/admin/discounts">优惠码管理</Link>,
  },
  {
    key: '/admin/files',
    icon: <CloudUploadOutlined />,
    label: <Link to="/admin/files">文件管理</Link>,
  },
  {
    key: '/admin/settings',
    icon: <SettingOutlined />,
    label: <Link to="/admin/settings">系统设置</Link>,
  },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { token } = theme.useToken();

  // 侧边栏折叠状态
  const [collapsed, setCollapsed] = useState(false);

  // 移除前台聊天插件（后台不需要）
  useEffect(() => {
    // 移除 Chatway 脚本
    const chatwayScript = document.getElementById('chatway');
    if (chatwayScript) {
      document.body.removeChild(chatwayScript);
    }
    // 移除 Chatway widget DOM 元素
    const chatwayWidgets = document.querySelectorAll('[id*="chatway"], [class*="chatway"]');
    chatwayWidgets.forEach((el) => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }, []);

  // 从 localStorage 读取用户信息，获取店铺名称
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();
  const storeName = storedUser?.storeName || '悦来悦好';

  // 根据当前路由高亮对应菜单项
  // 精确匹配 /admin，其余子路径前缀匹配
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/admin') return '/admin';
    const matched = menuItems
      .filter((item) => item.key !== '/admin' && path.startsWith(item.key))
      .sort((a, b) => b.key.length - a.key.length);
    return matched.length > 0 ? matched[0].key : '/admin';
  };

  // 退出登录处理
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={220}
        style={{
          background: token.colorBgContainer,
          borderRight: `1px solid ${token.colorBorderSecondary}`,
          overflow: 'auto',
          height: '100vh',
          position: 'sticky',
          top: 0,
          left: 0,
        }}
      >
        {/* Logo / 品牌区域 */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 20px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <Avatar
            size={32}
            style={{ background: token.colorPrimary, flexShrink: 0 }}
          >
            Y
          </Avatar>
          {!collapsed && (
            <Text
              strong
              style={{
                marginLeft: 10,
                fontSize: 15,
                color: token.colorText,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {storeName}
            </Text>
          )}
        </div>

        {/* 导航菜单 */}
        <Menu
          mode="inline"
          selectedKeys={[getSelectedKey()]}
          items={menuItems}
          style={{ border: 'none', marginTop: 8 }}
        />
      </Sider>

      {/* 右侧主区域 */}
      <Layout>
        {/* 顶部 Header */}
        <Header
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {/* 折叠按钮 */}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16 }}
          />

          {/* 右侧用户信息 + 退出按钮 */}
          <Space>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {storedUser?.email || '管理员'}
            </Text>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              退出登录
            </Button>
          </Space>
        </Header>

        {/* 内容区域，子路由渲染在此 */}
        <Content
          style={{
            padding: 24,
            background: token.colorBgLayout,
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
